import React from "react";

const BookingPrintTemplate = ({ booking, hospital }) => {
  const generatePrintHTML = () => {
    const currentDate = new Date().toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });

    // Check if it's token-based or time-based
    const isTokenBased =
      booking.timeSlot && booking.timeSlot.toLowerCase().includes("token");
    const appointmentTime = isTokenBased
      ? booking.timeSlot
      : booking.timeSlot || "N/A";

    // Hospital details from database or default
    const hospitalName = hospital?.name || "Medical Center";
    const hospitalAddress = hospital?.address || "123 Healthcare Avenue";
    const hospitalCity = hospital?.city || "";
    const hospitalState = hospital?.state || "";
    const hospitalPincode = hospital?.pincode || "";
    const hospitalPhone = hospital?.phoneNumber || "+91 1234567890";
    const hospitalEmail = hospital?.email || "info@medicalcenter.com";
    const hospitalWebsite = hospital?.website || "";

    // Construct full logo URL before inserting into HTML
    let hospitalLogo = null;
    if (hospital?.image) {
      if (hospital.image.startsWith("http")) {
        hospitalLogo = hospital.image;
      } else {
        const baseURL =
          process.env.REACT_APP_BASE_URL || "http://localhost:3040";
        // Split the path and encode only the filename part
        const pathParts = hospital.image.split("/");
        const filename = pathParts.pop();
        const encodedFilename = encodeURIComponent(filename);
        const basePath = pathParts.join("/");
        hospitalLogo = `${baseURL}${basePath}/${encodedFilename}`;
      }
      console.log("🖼️ Hospital Logo URL:", hospitalLogo);
    }

    // Format full address
    const fullAddress = [
      hospitalAddress,
      hospitalCity,
      hospitalState,
      hospitalPincode,
    ]
      .filter(Boolean)
      .join(", ");

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Appointment Prescription - ${booking.patientName}</title>
          <meta charset="UTF-8">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: A4;
              margin: 15mm;
            }
            
            body {
              font-family: 'Times New Roman', serif;
              background: white;
              padding: 0;
              font-size: 13px;
              line-height: 1.5;
              color: #000;
            }
            
            .prescription-container {
              width: 100%;
              height: 100vh;
              border: 3px solid #003366;
              padding: 0;
              position: relative;
              background: white;
              display: flex;
              flex-direction: column;
            }
            
            /* Compact Header Section */
            .header {
              background: linear-gradient(135deg, #003366 0%, #004d99 100%);
              color: white;
              padding: 12px 20px;
              border-bottom: 3px solid #002244;
              display: flex;
              align-items: center;
              gap: 15px;
            }
            
            .hospital-logo {
              width: 60px;
              height: 60px;
              object-fit: contain;
              background: white;
              padding: 5px;
              border-radius: 8px;
              border: 2px solid white;
              display: block;
            }
            
            .logo-placeholder {
              width: 60px;
              height: 60px;
              background: white;
              border-radius: 8px;
              border: 2px solid white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
            }
            
            .header-content {
              flex: 1;
            }
            
            .clinic-name {
              font-size: 20px;
              font-weight: bold;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 4px;
            }
            
            .clinic-info {
              font-size: 10px;
              opacity: 0.95;
              line-height: 1.4;
            }
            
            .clinic-info-line {
              margin-bottom: 2px;
            }
            
            /* Appointment Details Grid */
            .details-header {
              background: #f8f9fa;
              padding: 10px 15px;
              border-bottom: 2px solid #003366;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }
            
            .detail-group {
              display: flex;
              flex-direction: column;
              gap: 5px;
            }
            
            .detail-item {
              display: flex;
              align-items: baseline;
              gap: 8px;
            }
            
            .detail-label {
              font-weight: bold;
              color: #003366;
              font-size: 12px;
              min-width: 100px;
            }
            
            .detail-value {
              color: #000;
              font-size: 13px;
              font-weight: 500;
            }
            
            .detail-value.large {
              font-size: 15px;
              font-weight: bold;
              color: #003366;
            }
            
            /* Time Interval Badge */
            .time-badge {
              display: inline-block;
              background: #e8f4f8;
              padding: 4px 12px;
              border-radius: 4px;
              border: 1px solid #003366;
              font-weight: bold;
              color: #003366;
            }
            
            /* Status Badge - Now used for Booking Source */
            .status-badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: bold;
              text-transform: capitalize;
              letter-spacing: 0.5px;
            }
            
            .status-whatsapp {
              background: #dcfce7;
              color: #166534;
              border: 1px solid #22c55e;
            }
            
            .status-web {
              background: #dbeafe;
              color: #1e40af;
              border: 1px solid #3b82f6;
            }
            
            .status-booked {
              background: #dcfce7;
              color: #166534;
              border: 1px solid #166534;
            }
            
            .status-confirmed {
              background: #dbeafe;
              color: #1e40af;
              border: 1px solid #1e40af;
            }
            
            .status-completed {
              background: #f3f4f6;
              color: #374151;
              border: 1px solid #374151;
            }
            
            .status-cancelled {
              background: #fee2e2;
              color: #991b1b;
              border: 1px solid #991b1b;
            }
            
            /* Prescription Area */
            .prescription-body {
              flex: 1;
              padding: 20px;
              position: relative;
              background: white;
            }
            
            .rx-symbol {
              position: absolute;
              top: 15px;
              left: 20px;
              font-size: 36px;
              font-weight: bold;
              color: #003366;
              font-family: 'Brush Script MT', cursive;
            }
            
            .prescription-area {
              margin-top: 60px;
              min-height: 400px;
              padding: 15px;
              border: 1px dashed #ccc;
              background: #fafafa;
            }
            
            .prescription-lines {
              display: flex;
              flex-direction: column;
              gap: 25px;
            }
            
            .prescription-line {
              border-bottom: 1px solid #999;
              min-height: 25px;
            }
            
            /* Instructions */
            .instructions {
              margin-top: 20px;
              padding: 12px;
              background: #fff9e6;
              border: 1px solid #ffc107;
              border-radius: 4px;
              font-size: 11px;
              line-height: 1.6;
            }
            
            .instructions strong {
              color: #ff9800;
              display: block;
              margin-bottom: 5px;
            }
            
            /* Footer */
            .footer {
              padding: 15px 20px;
              border-top: 2px solid #003366;
              background: #f8f9fa;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .signature-area {
              text-align: center;
            }
            
            .signature-line {
              width: 180px;
              border-top: 2px solid #000;
              margin: 40px auto 5px;
            }
            
            .signature-label {
              font-size: 11px;
              color: #666;
              font-weight: bold;
            }
            
            .print-info {
              font-size: 10px;
              color: #999;
              text-align: right;
            }
            
            @media print {
              body {
                padding: 0;
              }
              
              .prescription-container {
                border: 3px solid #003366;
                height: auto;
                min-height: 100vh;
              }
            }
          </style>
        </head>
        <body>
          <div class="prescription-container">
            <!-- Header -->
            <div class="header">
              ${
                hospitalLogo
                  ? `<img src="${hospitalLogo}" alt="Hospital Logo" class="hospital-logo" onerror="this.style.display='none'" />`
                  : '<div class="logo-placeholder">🏥</div>'
              }
              <div class="header-content">
                <div class="clinic-name">🏥 ${hospitalName}</div>
                <div class="clinic-info">
                  <div class="clinic-info-line">📍 ${fullAddress}</div>
                  <div class="clinic-info-line">📞 ${hospitalPhone}${
      hospitalEmail ? ` | ✉️ ${hospitalEmail}` : ""
    }${hospitalWebsite ? ` | 🌐 ${hospitalWebsite}` : ""}</div>
                </div>
              </div>
            </div>
            
            <!-- Compact Details Header -->
            <div class="details-header">
              <!-- Left Column -->
              <div class="detail-group">
                <div class="detail-item">
                  <span class="detail-label">Patient:</span>
                  <span class="detail-value large">${
                    booking.patientName || "N/A"
                  }</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Phone:</span>
                  <span class="detail-value">${
                    booking.patientPhoneNumber || "N/A"
                  }</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Booking ID:</span>
                  <span class="detail-value">#${booking.bookingId}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Date:</span>
                  <span class="detail-value">${new Date(
                    booking.bookingDate
                  ).toLocaleDateString("en-IN", {
                    weekday: "short",
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}</span>
                </div>
              </div>
              
              <!-- Right Column -->
              <div class="detail-group">
                <div class="detail-item">
                  <span class="detail-label">Doctor:</span>
                  <span class="detail-value large">${
                    booking.doctorName || "N/A"
                  }</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Department:</span>
                  <span class="detail-value">${
                    booking.departmentName || "General"
                  }</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">${
                    isTokenBased ? "Token:" : "Time:"
                  }</span>
                  <span class="detail-value">
                    <span class="time-badge">${appointmentTime}</span>
                  </span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Booked Via:</span>
                  <span class="status-badge ${
                    booking.bookingSource === "whatsapp"
                      ? "status-whatsapp"
                      : "status-web"
                  }">
                    ${
                      booking.bookingSource === "whatsapp"
                        ? "📱 WhatsApp"
                        : "💻 Admin Portal"
                    }
                  </span>
                  <!-- Debug: ${booking.bookingSource || "undefined"} -->
                </div>
              </div>
            </div>
            
            <!-- Prescription Body -->
            <div class="prescription-body">
              <div class="rx-symbol">Rx</div>
              
              <div class="prescription-area">
                <div class="prescription-lines">
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                  <div class="prescription-line"></div>
                             <div class="prescription-line"></div>
                                        <div class="prescription-line"></div>
                  
                </div>
              </div>
              
        
            
            <!-- Footer -->
            <div class="footer">
              <div class="signature-area">
                <div class="signature-line"></div>
                <div class="signature-label">Doctor's Signature</div>
              </div>
              <div class="print-info">
                Printed: ${currentDate}<br>
                Computer Generated Document
              </div>
            </div>
          </div>
          
          <script>
            // Trigger print as soon as DOM is ready (faster than window.onload)
            if (document.readyState === 'complete') {
              window.print();
            } else {
              document.addEventListener('DOMContentLoaded', function() {
                window.print();
              });
            }
            
            // Auto-close window after printing
            window.onafterprint = function() {
              window.close();
            };
          </script>
        </body>
      </html>
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (printWindow) {
      printWindow.document.write(generatePrintHTML());
      printWindow.document.close();
    }
  };

  return handlePrint;
};

export default BookingPrintTemplate;
